/**
 * /operations/eos/retirement — M3X.EOS F.10 retirement scanner.
 *
 * Mutaba'aa-driven view: lists employees approaching Article 144
 * retirement age within the chosen horizon. Per-window buckets
 * (0-30d / 31-90d / 91-180d) for HR triage. Click any candidate
 * → opens the Khidmat planning packet bilingual preview.
 *
 * Pack ref §1.10.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../components/ui";
import { ErrorPanel, Loading } from "../components/Loading";
import { api } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

export function RetirementScannerPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const legalEntityId = useLegalEntityId();
  const [horizon, setHorizon] = useState(180);
  const [packetEmpId, setPacketEmpId] = useState<string | null>(null);
  const [packetLocale, setPacketLocale] = useState<"en" | "ar">(
    isAr ? "ar" : "en",
  );

  const candQ = useQuery({
    queryKey: ["retirement-candidates", legalEntityId, horizon],
    queryFn: () =>
      api.getRetirementCandidates({
        legal_entity_id: legalEntityId ?? undefined,
        horizon_days: horizon,
      }),
    enabled: Boolean(legalEntityId),
  });

  const packetQ = useQuery({
    queryKey: ["retirement-packet", packetEmpId, packetLocale],
    queryFn: () =>
      api.getRetirementPlanningPacket(packetEmpId ?? "", packetLocale),
    enabled: Boolean(packetEmpId),
  });

  if (candQ.isLoading) return <Loading />;
  if (candQ.error)
    return <ErrorPanel message={String((candQ.error as Error).message)} />;

  const data = candQ.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "retirement.title",
          "Retirement Scanner (Mutaba'aa)",
        )}
        subtitle={t(
          "retirement.subtitle",
          "Employees approaching Article 144 retirement age. Khidmat sends planning packets 90 days out; Tashghil opens draft EOS workflow at 30 days.",
        )}
      />

      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <label className="block text-xs font-medium text-text-secondary">
          {t("retirement.horizon", "Scan horizon (days)")}
        </label>
        <select
          value={horizon}
          onChange={(e) => setHorizon(Number(e.target.value))}
          className="mt-1 rounded-md border border-border-default p-2 text-sm"
        >
          <option value={90}>90 {t("retirement.days", "days")}</option>
          <option value={180}>180 {t("retirement.days", "days")}</option>
          <option value={365}>365 {t("retirement.days", "days")}</option>
          <option value={730}>730 {t("retirement.days", "days")}</option>
        </select>
      </div>

      {data && (
        <div className="grid gap-4 sm:grid-cols-4">
          <SummaryCard
            label={t("retirement.total", "Total candidates")}
            count={data.total_count}
            tone="neutral"
          />
          <SummaryCard
            label={t("retirement.window_0_30", "Within 30 days")}
            count={data.by_window_days["0-30"]}
            tone="rose"
          />
          <SummaryCard
            label={t("retirement.window_31_90", "31-90 days")}
            count={data.by_window_days["31-90"]}
            tone="amber"
          />
          <SummaryCard
            label={t("retirement.window_91_180", "91-180 days")}
            count={data.by_window_days["91-180"]}
            tone="emerald"
          />
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <h3 className="mb-3 text-sm font-semibold text-text-primary">
          {t("retirement.candidates_title", "Candidates")}
        </h3>
        {data?.candidates.length === 0 ? (
          <p className="text-sm text-text-tertiary">
            {t(
              "retirement.no_candidates",
              "No employees approach retirement in the scan horizon.",
            )}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-text-tertiary">
              <tr className="border-b border-slate-200">
                <th className="py-2 text-start text-xs uppercase">
                  {t("retirement.code", "Code")}
                </th>
                <th className="py-2 text-start text-xs uppercase">
                  {t("retirement.name", "Name")}
                </th>
                <th className="py-2 text-end text-xs uppercase">
                  {t("retirement.age", "Age")}
                </th>
                <th className="py-2 text-end text-xs uppercase">
                  {t("retirement.days_until", "Days Until")}
                </th>
                <th className="py-2 text-start ps-3 text-xs uppercase">
                  {t("retirement.date", "Retirement Date")}
                </th>
                <th className="py-2 text-end text-xs uppercase">
                  {t("retirement.action", "Action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.candidates.map((c) => (
                <tr key={c.employee_id} className="border-b border-slate-100">
                  <td className="py-2 font-mono text-xs">{c.employee_code}</td>
                  <td className="py-2">{isAr ? c.name_ar : c.name_en}</td>
                  <td className="py-2 text-end font-mono">{c.retirement_age}</td>
                  <td className="py-2 text-end font-mono">
                    {c.days_until_retirement}
                  </td>
                  <td className="py-2 ps-3 font-mono text-xs">
                    {c.estimated_retirement_date}
                  </td>
                  <td className="py-2 text-end">
                    <div className="flex flex-col items-end gap-1">
                      <button
                        type="button"
                        onClick={() => setPacketEmpId(c.employee_id)}
                        className="text-xs text-brand-blue hover:underline"
                      >
                        {t("retirement.view_packet", "✦ View packet")}
                      </button>
                      <FireRetirementButton
                        employeeId={c.employee_id}
                        retirementDate={c.estimated_retirement_date}
                        legalEntityId={legalEntityId ?? ""}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {packetEmpId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-surface-default p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <h2 className="text-base font-bold text-text-primary">
                {t(
                  "retirement.packet_title",
                  "Khidmat — Retirement Planning Packet",
                )}
              </h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPacketLocale("en")}
                  className={`rounded border px-2 py-0.5 text-xs ${
                    packetLocale === "en"
                      ? "border-brand-blue bg-brand-blue/5 text-brand-blue"
                      : "border-slate-300 text-text-tertiary"
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setPacketLocale("ar")}
                  className={`rounded border px-2 py-0.5 text-xs ${
                    packetLocale === "ar"
                      ? "border-brand-blue bg-brand-blue/5 text-brand-blue"
                      : "border-slate-300 text-text-tertiary"
                  }`}
                >
                  AR
                </button>
                <button
                  type="button"
                  onClick={() => setPacketEmpId(null)}
                  className="text-text-tertiary hover:text-text-primary"
                >
                  ×
                </button>
              </div>
            </div>
            {packetQ.isLoading && (
              <p className="text-xs text-text-tertiary">
                {t("common.loading", "Loading…")}
              </p>
            )}
            {packetQ.data && (
              <div className="space-y-2 rounded-md border border-brand-blue/30 bg-brand-blue/5 p-4">
                <h3 className="text-sm font-semibold text-brand-deep">
                  {packetQ.data.packet.subject}
                </h3>
                <pre
                  dir={packetLocale === "ar" ? "rtl" : "ltr"}
                  className="whitespace-pre-wrap text-xs text-text-secondary"
                >
                  {packetQ.data.packet.body}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: "neutral" | "emerald" | "amber" | "rose";
}) {
  const cls =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "amber"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : tone === "rose"
          ? "border-rose-200 bg-rose-50 text-rose-900"
          : "border-slate-200 bg-surface-default text-text-primary";
  return (
    <div className={`rounded-lg border p-4 ${cls}`}>
      <div className="text-xs uppercase tracking-wider">{label}</div>
      <div className="mt-1 font-mono text-3xl font-bold">{count}</div>
    </div>
  );
}

function FireRetirementButton({
  employeeId,
  retirementDate,
  legalEntityId,
}: {
  employeeId: string;
  retirementDate: string;
  legalEntityId: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function fire() {
    if (!legalEntityId) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = (await api.initiateRetirementTermination({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        retirement_date: retirementDate,
        auto_triggered: true,
      })) as { id: string };
      setMsg(`✓ M18 ${r.id.slice(0, 8)}`);
    } catch (e) {
      setMsg(String(e).slice(0, 50));
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={fire}
      disabled={busy || !legalEntityId}
      className="text-[10px] text-brand-purple hover:underline disabled:opacity-50"
    >
      {busy
        ? "…"
        : msg ?? t("retirement.fire_m18", "✦ Open M18 termination")}
    </button>
  );
}

export default RetirementScannerPage;
